import {useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {Add, Close, Remove} from '@mui/icons-material';
import {FiTrash2} from 'react-icons/fi';
import {FaListUl} from 'react-icons/fa6';
import {CheckRounded, CloseRounded, PriorityHighRounded} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';
import {PlannerItemDeleteData, PlannerActivityItem, PlannerItemRegisterData} from '../planner-item';
import {translations} from '../../../../../../../../i18n/translations';
import {ComboActions} from '../../../../../../../../components/combo-actions/combo-actions';
import {Pallete} from '../../../../../../../../theme/bluebird';
import {UserAvatar} from '../../shared/user-avatar';
import {useAuth} from '../../../../../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../../../../../backend/enums/role-permission-type';
import {SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING} from '../../../../../../../../utils/constants';

type PlannerActvityItemMenuProps = {
  item: PlannerActivityItem;
  onExpand: (expanded: boolean) => void;
  onClose: () => void;
  onDelete: (data: PlannerItemDeleteData[]) => void;
  onRegister: (data: PlannerItemRegisterData) => void;
  onPlan: (data: PlannerItemRegisterData) => void;
};

export function PlannerActivityItemMenu({item, ...props}: PlannerActvityItemMenuProps) {
  const [expanded, setExpanded] = useState(true);
  const {t} = useTranslation();
  const {hasWritePermission} = useAuth();

  const canWrite =
    hasWritePermission(RolePermissionType.Calendar) &&
    hasWritePermission(SECTOR_ID_TO_CALENDAR_PERMISSION_TYPE_MAPPING[item.activitiesSector.id]);

  return (
    <Stack flexDirection="column">
      {/* -------------- HEADER -------------- */}
      <Stack flexDirection="row">
        {/* --------- ACTIVITY TYPE --------- */}
        <Typography
          sx={{
            flex: 1,
            fontWeight: 500,
            fontSize: '16px',
            color: 'primary.main',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            marginBottom: '2px',
          }}
        >
          {item.activitiesType.name}
        </Typography>

        {/* ------- ACTIONS ------- */}
        <ComboActions
          actions={[
            {
              label: t(translations.planner.item_menu.activity.action.delete),
              icon: {element: FiTrash2, size: '14px'},
              disabled: !canWrite,
              disabledTooltip: t(translations.planner.item_menu.activity.action.delete_disabled),
              callback: () => {
                const data: PlannerItemDeleteData[] = item.users.map((user) => ({
                  item,
                  user,
                }));
                props.onDelete(data);
              },
            },
          ]}
          triggerSX={{
            color: 'gray.main',
            width: '32px',
            height: '32px',
          }}
        />

        {/* ------- CLOSE BUTTON ------- */}
        <IconButton sx={{width: 32, height: 32, color: 'gray.main'}} onClick={props.onClose}>
          <Close />
        </IconButton>
      </Stack>

      {/* -------------- BODY -------------- */}
      <Stack gap="12px">
        {/* ---- DATE & ACTIVITY GROUP ----- */}
        <Stack gap="6px">
          {/* ------ DATE ------ */}
          <Typography
            sx={{
              fontSize: '14px',
              color: 'gray.main',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {item.date.format.day} • {item.date.format.hour}
          </Typography>

          {/* ---- ACTIVITY GROUP ----- */}
          <Typography
            sx={{
              fontSize: '14px',
              color: 'gray.main',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {item.activitiesGroup.name}
          </Typography>
        </Stack>

        {/* ------- USERS LIST ------- */}
        <Accordion
          expanded={expanded}
          sx={{
            boxShadow: 'none',
            '& .MuiAccordionSummary-root': {
              backgroundColor: 'blue.hover',
              borderRadius: '5px',
              margin: 0,
              padding: '6px 4px',
              height: '32px',
              minHeight: 'inherit',
            },
            '& .MuiAccordionSummary-root.Mui-expanded': {
              minHeight: 'inherit',
            },
            '&.MuiAccordion-root': {
              margin: 0,
            },
            '&.MuiAccordion-root:before': {
              display: 'none',
            },
            '& .MuiAccordionDetails-root': {
              padding: 0,
            },
          }}
          onChange={() => setExpanded(!expanded)}
        >
          {/* ------- USERS EXPAND BTN ------- */}
          <AccordionSummary
            expandIcon={
              expanded ? (
                <Remove sx={{width: 20, color: 'gray.main'}} />
              ) : (
                <Add sx={{width: 20, color: 'gray.main'}} />
              )
            }
            onClick={() => props.onExpand(!expanded)}
          >
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '14px',
                color: 'gray.main',
              }}
            >
              {t(translations.planner.item_menu.activity.users)} ({item.users.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack flexDirection="column" sx={{marginTop: '2px'}}>
              {/* ------- USERS ------- */}
              {item.users.map((user, index) => (
                <Stack
                  key={`${index}_${user.id}`}
                  flexDirection="row"
                  height="32px"
                  alignItems="center"
                  padding="0 4px"
                  gap="9px"
                  color="gray.main"
                  onClick={() => {
                    props.onRegister({item, user, canWrite});
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'blue.hover',
                      cursor: 'pointer',
                    },
                  }}
                >
                  {/* ------- USER PIC ------- */}
                  <UserAvatar
                    size="s"
                    disableTooltip
                    name={user.name}
                    picture={user.picture}
                    socialResponses={user.socialResponses}
                  />
                  {/* ------- USER NAME ------- */}
                  <Typography sx={{flex: 1, fontWeight: 500, fontSize: '14px'}}>
                    {user.name}
                  </Typography>

                  {/* --- USER ACTIVITY INDICATION --- */}
                  {(() => {
                    // --- HANDLED ---
                    if (user.careTask.handled) {
                      // - DONE -
                      if (user.careTask.done)
                        return (
                          <CheckRounded
                            style={{color: Pallete.green.base, width: '18px', height: '18px'}}
                          />
                        );
                      // - NOT DONE -
                      else
                        return (
                          <CloseRounded
                            style={{color: Pallete.red.base, width: '18px', height: '18px'}}
                          />
                        );
                    }

                    // --- UNATTENDED ---
                    if (user.careTask.unattended)
                      return (
                        <PriorityHighRounded
                          style={{color: Pallete.yellow.base, width: '18px', height: '18px'}}
                        />
                      );
                  })()}

                  {/* ------- ACTIONS ------- */}
                  <ComboActions
                    actions={[
                      {
                        label: t(translations.planner.item_menu.activity.action.register),
                        icon: {element: CheckRounded, size: '14px'},
                        callback: () => props.onRegister({item, user, canWrite}),
                      },
                      {
                        label: t(translations.planner.item_menu.activity.action.plan),
                        icon: {element: FaListUl, size: '14px'},
                        callback: () => props.onPlan({item, user, canWrite}),
                      },
                      {
                        label: t(translations.planner.item_menu.activity.action.delete),
                        icon: {element: FiTrash2, size: '14px'},
                        callback: () => props.onDelete([{item, user}]),
                        disabled: !canWrite,
                        disabledTooltip: t(
                          translations.planner.item_menu.activity.action.delete_disabled
                        ),
                      },
                    ]}
                    triggerSX={{
                      color: 'gray.main',
                      width: '30px',
                      height: '28px !important',
                    }}
                  />
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Stack>
  );
}
